import type { FormatedProductPrice } from "@dransay/api";
import {
	type DeliveryMethodKind,
	VENDOR_AGGREGATION_GROUP,
} from "@dransay/api/models";

export type ExclusivePdpLayoutVariant = "hidden" | "compact" | "carousel";

export type ExclusiveCompactLayoutMode =
	| "static"
	| "static-centered"
	| "carousel";

/** Product count at which the PDP switches from compact to carousel layout. */
export const MIN_CAROUSEL_PRODUCT_COUNT = 4;

/** Product count that uses the compact carousel (3 cards) layout. */
export const COMPACT_CAROUSEL_PRODUCT_COUNT = 3;

/** Product count that centers two cards on mobile. */
export const COMPACT_CENTERED_PRODUCT_COUNT = 2;

export function excludeCurrentProductFromExclusiveList(
	products: FormatedProductPrice[],
	productId: string,
): FormatedProductPrice[] {
	return products.filter((item) => item.id !== productId);
}

export function getExclusivePdpLayoutVariant(
	count: number,
): ExclusivePdpLayoutVariant {
	if (count === 0) {
		return "hidden";
	}
	if (count < MIN_CAROUSEL_PRODUCT_COUNT) {
		return "compact";
	}
	return "carousel";
}

export function getExclusiveCompactLayoutMode(
	count: number,
): ExclusiveCompactLayoutMode {
	if (count === COMPACT_CAROUSEL_PRODUCT_COUNT) {
		return "carousel";
	}
	if (count === COMPACT_CENTERED_PRODUCT_COUNT) {
		return "static-centered";
	}
	return "static";
}

/** Tailwind visibility classes for the PDP exclusive section background pattern. */
export function getExclusiveProductsBackgroundPatternVisibilityClass(
	productCount: number,
): string {
	if (productCount === COMPACT_CENTERED_PRODUCT_COUNT) {
		return "max-md:hidden";
	}
	if (productCount >= COMPACT_CAROUSEL_PRODUCT_COUNT) {
		return "hidden";
	}
	return "";
}

export function shouldShowExclusiveProductsPdpSection(
	cartItemCount: number,
	exclusiveProductCount: number,
): boolean {
	return cartItemCount === 0 && exclusiveProductCount > 0;
}

export function shouldUseDesktopStickyPdpCta(
	showExclusiveProductsSection: boolean,
): boolean {
	return !showExclusiveProductsSection;
}

export function getExclusiveProductsFromRefetchResponse(
	products: FormatedProductPrice[] | undefined,
	productId: string,
): FormatedProductPrice[] {
	if (!products) {
		return [];
	}
	return excludeCurrentProductFromExclusiveList(products, productId);
}

export function isConcreteVendorId(vendorId: string | undefined): boolean {
	const aggregatedVendorIds = Object.values(
		VENDOR_AGGREGATION_GROUP,
	) as string[];
	return !!vendorId && !aggregatedVendorIds.includes(vendorId);
}

export function shouldReuseLoaderExclusiveProducts({
	initialVendorId,
	initialDeliveryMethodKind,
	displayedVendorId,
	displayedDeliveryMethodKind,
}: {
	initialVendorId: string | undefined;
	initialDeliveryMethodKind: DeliveryMethodKind | undefined;
	displayedVendorId: string | undefined;
	displayedDeliveryMethodKind: DeliveryMethodKind | undefined;
}): boolean {
	if (!isConcreteVendorId(displayedVendorId)) {
		return false;
	}

	if (!isConcreteVendorId(initialVendorId)) {
		return false;
	}

	return (
		initialVendorId === displayedVendorId &&
		initialDeliveryMethodKind === displayedDeliveryMethodKind
	);
}

export function buildExclusiveProductsFetchUrl(
	vendorId: string | undefined,
	deliveryMethodKind: DeliveryMethodKind | undefined,
): string {
	const params = new URLSearchParams({ type: "exclusive" });
	if (vendorId) {
		params.set("vendorId", vendorId);
	}
	if (deliveryMethodKind) {
		params.set("deliveryMethod", deliveryMethodKind);
	}
	return `/api/curated-products?${params.toString()}`;
}
